import { useId } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@attendance/ui/utils';
import { useFeatures } from '../../stores/brandingStore.js';
import { NavBadge } from './NavBadge.js';
import { NavLiveCount } from './NavLiveCount.js';
import {
  COLLAPSED_LABEL,
  EYEBROW,
  HEADING_ICON,
  NAV_ICON,
  RAIL_DIVIDER,
  RAIL_FOCUS,
  rowClasses,
} from './railSkin.js';
import { navLabelOf, type NavLeaf, type NavGroup } from './navItems.js';
import { PILLAR_ICON_CLASS, PILLAR_NAV_CLASS, pillarForPath, type Pillar } from './pillars.js';

/* ===========================================================================
 * WHAT THE RAIL DRAWS: a heading, a destination, and a group of them.
 *
 * Lifted out of `Sidebar.tsx`, which is the rail's SHELL — its width, its
 * drawer, its surface switch and which lists it renders. These are the rows
 * inside it, and every argument for how they look (why a group's children
 * carry no indent, why the active marker is a positioned span rather than a
 * border, why a one-item group renders as a bare row) stays with the code
 * that draws them.
 * ======================================================================== */

/**
 * The heading over the personal list. Static text, not a disclosure: this
 * section has no chevron because it has nothing to hide behind one — it is the
 * whole of the personal workspace.
 *
 * It exists for the COMBINED console above all (self-service on a tenant whose
 * users also hold management rights): there the personal rows and the first
 * admin group render one after the other, and until now a hairline was the only
 * thing between "your leave" and "everyone's leave".
 *
 * A real `h2` with the list labelled by it, rather than a styled div — the rows
 * under it are a named group to a screen reader for the same reason they are
 * one to the eye. The label clips rather than hides when the rail collapses
 * (see COLLAPSED_LABEL), so the name survives at 52px even though the glyph is
 * all that is drawn.
 *
 * The id is generated (`useId`) rather than written down: `aria-labelledby`
 * resolves to the FIRST element with a matching id in the document, so a
 * literal would silently point a second rail's list at the first rail's
 * heading. Only one rail is mounted today; a constant would make that an
 * invariant nobody knows they are holding.
 */
export function SectionHeading({
  icon: Icon,
  label,
  collapsed,
  headingId,
}: {
  icon: LucideIcon;
  label: string;
  collapsed: boolean;
  headingId: string;
}): JSX.Element {
  return (
    <h2
      id={headingId}
      title={collapsed ? label : undefined}
      className={cn(
        EYEBROW,
        'text-nav-on-surface-faint',
        collapsed && 'md:justify-center md:ps-0 md:pe-0'
      )}
    >
      <Icon {...HEADING_ICON} className="shrink-0" aria-hidden />
      <span className={cn('truncate', collapsed && COLLAPSED_LABEL)}>{label}</span>
    </h2>
  );
}

/**
 * The marker on the current row: a 3px bar on the row's inline-START edge, in
 * `--nav-active`, inset 12px from the pill's top and bottom — which is a 20px
 * bar on a 44px row, the handoff's own proportion, and far enough in to clear
 * the pill's 10px corner radius so the bar's rounded ends never poke out past
 * the curve.
 *
 * It is an absolutely positioned span rather than the `border-s` it replaces
 * because the row is now a rounded pill, and a 3px border on a 10px corner draws
 * a thickening crescent around two corners instead of a bar. `start-0` is
 * logical — `inset-inline-start` — so it sits on the rail's own edge in Arabic
 * too, which is the whole reason this was never `border-l`.
 *
 * Out of flow, so a row gains and loses it without moving; `aria-hidden` and
 * empty, because the state it draws is already announced by `aria-current` on
 * the row itself.
 */
export function ActiveMarker({ pillar }: { pillar: Pillar }): JSX.Element {
  /* THE MARKER CARRIES THE AREA'S ACCENT — one of two places a pillar hue
     appears; both read the same resolver so they cannot disagree. People IS
     the brand and its lifted form IS `--nav-active`, so the roster renders
     exactly what it always did and the other four were levelled against it. */
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-3 start-0 w-[3px] rounded-full',
        PILLAR_NAV_CLASS[pillar]
      )}
    />
  );
}

/**
 * One destination.
 *
 * The `nested` prop is GONE, and its indent with it. A group's children used to
 * be pushed in by 24px to say "these belong to the heading above". They now sit
 * at the same inset as every other row, because the heading above them is no
 * longer a row: it is a 12px uppercase eyebrow, dimmer than its children, with a
 * hairline over it (see `CollapsibleGroup`). Eyebrow plus hairline plus indent
 * was three signals for one relationship, and the indent was the one that read
 * as a THIRD level of hierarchy the app does not have — every group in
 * `navItems.ts` is exactly one deep.
 */
export function Leaf({
  item,
  activeTo,
  collapsed,
}: {
  item: NavLeaf;
  activeTo?: string;
  collapsed: boolean;
}): JSX.Element {
  /* From the ROW'S destination, not the current route: the marker only draws
     while this row is active. `item.pillar` wins for a row in another area. */
  const pillar = item.pillar ?? pillarForPath(item.to);
  const { t } = useTranslation();
  const features = useFeatures();
  // Active only when THIS item is the most-specific match (computed by the
  // Sidebar), not on any prefix match — so a parent and its child are never
  // both highlighted.
  const isActive = item.to === activeTo;
  const naming = navLabelOf(item, features);
  const label = t(naming.labelKey, naming.label);
  /*
   * A PLAIN `Link`, DELIBERATELY — this row is not a `NavLink`.
   *
   * `NavLink` decides "am I current" for itself, by PREFIX, and it writes
   * `aria-current="page"` from that decision. Passing `aria-current={undefined}`
   * does not opt out: NavLink reads the prop as the VALUE to use when it
   * considers itself active and defaults an absent one back to `"page"`. So the
   * marker a screen reader heard and the marker the eye saw were computed by two
   * different rules. On /employees/import, /leave/manage, /corrections/approvals
   * and /settings/device-approvals — four real routes — the parent row was
   * announced as the current page alongside its child, while only the child was
   * lit. `Link` leaves the decision here, where the highlight is already made.
   */
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={rowClasses({ collapsed, active: isActive })}
    >
      {isActive && <ActiveMarker pillar={pillar} />}
      {/* The current row's glyph carries the brand. Collapsed, the label and its
          weight are gone and the icon is most of what is left, so the state has
          to live in the icon too — not only in the bar beside it. Measured over
          the active row's own ground (the /10 layer, not the bare rail): 4.38:1
          light / 5.17:1 dark, against the 3:1 floor for a non-text mark. */}
      <item.icon
        {...NAV_ICON}
        className={cn('shrink-0', isActive && PILLAR_ICON_CLASS[pillar])}
        aria-hidden
      />
      <span className={cn('truncate', collapsed && COLLAPSED_LABEL)}>{label}</span>
      {/* Both trailing marks are DROPPED, not clipped, while the rail is
          collapsed to icons — a pill or a live dot with no row to sit on lands
          over the glyph and reads as part of the icon. Unlike a label, neither
          is a name for anything: the row's own label is already `sr-only` and
          still carries the destination, so nothing leaves the accessibility
          tree that a reader needs to get somewhere. */}
      {item.badge && !collapsed && <NavBadge kind={item.badge} />}
      {item.live && !collapsed && <NavLiveCount />}
    </Link>
  );
}

export function NavGroupSection({
  group,
  activeTo,
  collapsed,
  open,
  onToggle,
}: {
  group: NavGroup;
  activeTo?: string;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const headingId = useId();
  const listId = useId();
  const title = t(group.titleKey, group.title);

  /* A GROUP OF ONE IS NOT A GROUP — it is a row wearing a heading and a
   * chevron.
   *
   * Every group here is role- and feature-filtered before it arrives, so how
   * many rows a section actually has depends on the tenant. A section that
   * comes out at one link is a collapsible thing you open to find a single
   * destination, plus a heading that says the same word the row does — and on
   * the lean preset that is what several of them become.
   *
   * The app has paid for this twice already, and both times the fix was to
   * hand-move the offending row out: "Time & pay" was retired because a MANAGER
   * saw one link in it, and the attendance calendar was promoted to a top-level
   * row after being "the ONLY survivor of its group" (see navItems.ts). Both
   * repairs were to the MEMBERSHIP, so both only fixed the tenant shape that
   * had been noticed — and both constrained every later grouping decision to
   * whatever survives the leanest preset.
   *
   * Fixing it here instead makes the rail robust for every combination of
   * features and roles at once, and gives grouping back to what a reader
   * expects to find together rather than to what the feature flags allow. The
   * row keeps its icon, its badge and its active marker; it simply loses a
   * heading it was the only member of. */
  const only = group.items.length === 1 ? group.items[0] : null;
  if (only) {
    return (
      <div className={cn('mt-rail-group border-t pt-4', RAIL_DIVIDER)}>
        <Leaf item={only} activeTo={activeTo} collapsed={collapsed} />
      </div>
    );
  }

  /* NO DISCLOSURE WHILE THE RAIL IS COLLAPSED TO ICONS.
     At 52px there is no label to hide and no room for a chevron — the heading
     is already `sr-only` — so a toggle there would be a control with nothing
     to show for either state. The section renders static, and the reader's
     stored preference is untouched and waiting when they widen the rail. */
  const list = (
    <ul id={listId} className="flex flex-col gap-0.5" aria-labelledby={headingId}>
      {group.items.map((item) => (
        <li key={item.to}>
          <Leaf item={item} activeTo={activeTo} collapsed={collapsed} />
        </li>
      ))}
    </ul>
  );

  if (collapsed) {
    return (
      <div className={cn('mt-rail-group border-t pt-4', RAIL_DIVIDER)}>
        <SectionHeading
          headingId={headingId}
          icon={group.icon}
          label={title}
          collapsed={collapsed}
        />
        {list}
      </div>
    );
  }

  return (
    /* A DISCLOSURE, at the owner's call.
     *
     * The handoff draws these as static sections and they were built that way
     * for a while — the mocks have no chevron and nothing that can be shut. The
     * owner wants them collapsible, which outranks the mock (§0: the rules
     * file, then the mock, and above both the person whose product it is).
     *
     * The reason they were taken away is fixed rather than reintroduced: a
     * group left shut used to STAY shut across sessions, so a reader came back
     * to a rail missing rows and no hint that the page they were on lived
     * inside one of them. The group owning the current route is now forced open
     * (see the call site), so the stranding state cannot occur.
     *
     * The BUTTON IS INSIDE THE HEADING rather than wrapping it: an `h2` that is
     * itself a button is announced as one thing, and a screen reader's heading
     * list — the way people skim a nav — would read "button" where it wants a
     * section name. This way the heading stays a heading, the button is the
     * control, and `aria-controls` ties it to the list it opens. */
    <div className={cn('mt-rail-group border-t pt-4', RAIL_DIVIDER)}>
      <h2 id={headingId}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={listId}
          className={cn(
            EYEBROW,
            'w-full text-nav-on-surface-faint',
            'transition-colors duration-fast ease-standard hover:text-nav-on-surface',
            /* `RAIL_FOCUS`, not a hand-written copy of it. This line used to
               spell the ring out — same four utilities, in `outline-nav-active`
               — which happened to be the RIGHT colour while `RAIL_FOCUS` itself
               was still on the invisible `outline-ring`. Two dialects, one of
               them broken, and the broken one was the shared constant. */
            RAIL_FOCUS
          )}
        >
          <group.icon {...HEADING_ICON} className="shrink-0" aria-hidden />
          <span className="truncate">{title}</span>
          {/* `ms-auto` so the glyph sits on the trailing edge in both writing
              directions. The rotation is on the BLOCK axis — down when open,
              pointing at the inline-start edge when shut — so it needs no RTL
              mirroring: a chevron rotated -90° reads as "closed" either way. */}
          <ChevronDown
            {...HEADING_ICON}
            aria-hidden
            className={cn(
              'ms-auto shrink-0 transition-transform duration-fast ease-standard',
              !open && '-rotate-90'
            )}
          />
        </button>
      </h2>
      {open && list}
    </div>
  );
}
